<template>
  <hero-animation-wrapper>
    <template slot="lines">
      <hero-lines ref="wrapper">
        <hero-line ref="lines" v-for="i in 10" :key="i"/>
      </hero-lines>
    </template>
    <template slot="typo">
      <hero-text
        :text="creativity.text"
        :visible="creativity.visible"
        :invert="creativity.invert"
        name="creativity"
      />

      <hero-text
        :text="execution.text"
        :visible="execution.visible"
        :invert="execution.invert"
        :scale="execution.scale"
        align="left"
        name="execution"
      />

      <hero-text
        :text="delivery.text"
        :visible="delivery.visible"
        :invert="delivery.invert"
        :scale="delivery.scale"
        name="delivery"
      />

      <hero-text
        :text="love.text"
        :visible="love.visible"
        :invert="love.invert"
        align="left"
        alone
        name="love"
      >
        <hero-seven/>
      </hero-text>
    </template>
  </hero-animation-wrapper>
</template>

<script>
import { mapGetters } from 'vuex'

import HeroLine from '@/components/HeroAnimation/HeroLine'
import HeroLines from '@/components/HeroAnimation/HeroLines'

import { createStepTimeline } from '@/assets/js/hero/helpers'
import createCreativityLine from '@/assets/js/hero/creativity'
import createExecutionLine from '@/assets/js/hero/execution'
import createDeliveryLine from '@/assets/js/hero/delivery'
import createLoveLine from '@/assets/js/hero/love'
import createEmptyLine from '@/assets/js/hero/empty'
import createFinishLine from '@/assets/js/hero/finish'

import HeroText from '@/components/HeroAnimation/HeroText'
import HeroSeven from '@/components/HeroAnimation/HeroSeven'
import HeroAnimationWrapper from '@/components/HeroAnimation/HeroAnimationWrapper'

export default {
  components: {
    HeroLine,
    HeroLines,
    HeroText,
    HeroSeven,
    HeroAnimationWrapper
  },
  data () {
    return {
      creativity: {
        text: 'creativity<br>first',
        visible: true,
        invert: false
      },
      execution: {
        text: 'execution<br>paramount',
        visible: false,
        invert: false,
        scale: true
      },
      delivery: {
        text: 'delivery<br>wow',
        visible: false,
        invert: false,
        scale: false
      },
      love: {
        text: 'love',
        visible: false,
        invert: false
      }
    }
  },
  computed: {
    ...mapGetters(['isLoading'])
  },
  watch: {
    isLoading () {
      if (!this.isLoading) {
        setTimeout(() => {
          this.masterTimeline.play()
        }, 1500)
      }
    }
  },
  mounted () {
    /* eslint-disable */
    const lines = this.$refs.lines.map(line => line.$el)

    // Prepare animation
    TweenLite.set(lines, { scaleX: 0 })

    const params = [
      { showOffset: 0.33, hideOffset: 0.55, showOffset2: 0.23, hideScale: 0.164, showScale: 0.95 },
      { showOffset: 0.8, hideOffset: 0, showOffset2: 0.16, hideScale: 0.168, showScale: 0.5 },
      { showOffset: 0.7, hideOffset: 0.13, showOffset2: 0.1, hideScale: 0.174, showScale: 0.6 },
      { showOffset: 0, hideOffset: 0.73, showOffset2: 0.33, hideScale: 0.154, showScale: 0.9 },
      { showOffset: 0.61, hideOffset: 0.26, showOffset2: 0, hideScale: 0.2, showScale: 0.66 },
      { showOffset: 0.26, hideOffset: 0.53, showOffset2: 0.16, hideScale: 0.176, showScale: 0.95 },
      { showOffset: 0.5, hideOffset: 0.38, showOffset2: 0, hideScale: 0.2, showScale: 0.8 },
      { showOffset: 0.53, hideOffset: 0.16, showOffset2: 0.4, hideScale: 0.154, showScale: 0.77 },
      { showOffset: 0.81, hideOffset: 0, showOffset2: 0.16, hideScale: 0.176, showScale: 0.5 },
      { showOffset: 0.53, hideOffset: 0.25, showOffset2: 0.2, hideScale: 0.164, showScale: 0.77 }
    ]

    // 1. Creativity
    const creativityLines = lines.map((line, i) => (
      createCreativityLine(line, params[i])
    ))
    const creativityTimeline = createStepTimeline(creativityLines)

    // 2. Execution
    const executionLines = lines.map((line, i) => (
      createExecutionLine(line, params[i])
    ))
    const executionTimeline = createStepTimeline(executionLines)

    // 3. Delivery
    const deliveryLines = lines.map((line, i) => (
      createDeliveryLine(line, params[i])
    ))
    const deliveryTimeline = createStepTimeline(deliveryLines)

    // 4. Love (same as execution)
    const loveLines = lines.map((line, i) => (
      createLoveLine(line, params[i])
    ))
    const loveTimeline = createStepTimeline(loveLines)

    // 5. Empty
    const emptyLines = lines.map((line, i) => (
      createEmptyLine(line, params[i])
    ))
    const emptyTimeline = createStepTimeline(emptyLines)

    // 6. Finish
    const finishLines = lines.map((line, i) => (
      createFinishLine(line, params[i])
    ))
    const finishTimeline = createStepTimeline(finishLines)

    // helpers
    const invert = name => () => {
      this[name].invert = !this[name].invert
    }

    const toggleVisibility = name => () => {
      this[name].visible = !this[name].visible
    }

    const toggleScale = name => () => {
      this[name].scale = !this[name].scale
    }

    // Create animation
    const masterTimeline = new TimelineMax({
      paused: true,
      onComplete () {
        setTimeout(this.restart.bind(this), 3000)
      }
    })
    masterTimeline
      .add(creativityTimeline)
      .add(executionTimeline)
      .add(deliveryTimeline)
      .add(loveTimeline)
      .add(emptyTimeline)
      .add(finishTimeline)

    // Creativity Text
    masterTimeline
      .addCallback(invert('creativity'), 0.25)
      .addCallback(invert('creativity'), 0.35)
      .addCallback(invert('creativity'), 0.4)
      .addCallback(invert('creativity'), 0.45)
      .addCallback(invert('creativity'), 0.9)
      .addCallback(invert('creativity'), 1.9)
      .addCallback(toggleVisibility('creativity'), 2.4)

    // Execution Text
    masterTimeline
      .addCallback(toggleVisibility('execution'), 2.7)
      .addCallback(invert('execution'), 3.27)
      .addCallback(invert('execution'), 3.39)
      .addCallback(invert('execution'), 3.48)
      .addCallback(invert('execution'), 3.57)
      .addCallback(invert('execution'), 3.8)
      .addCallback(toggleVisibility('execution'), 3.9)
      .addCallback(toggleVisibility('execution'), 4.03)
      .addCallback(toggleScale('execution'), 4.1)
      .addCallback(toggleVisibility('execution'), 4.4)

    // Delivery Text
    masterTimeline
      .addCallback(toggleVisibility('delivery'), 4.6)
      .addCallback(invert('delivery'), 4.85)
      .addCallback(invert('delivery'), 4.95)
      .addCallback(invert('delivery'), 5.45)
      .addCallback(invert('delivery'), 5.55)
      .addCallback(invert('delivery'), 5.64)
      .addCallback(invert('delivery'), 5.95)
      .addCallback(toggleScale('delivery'), 5.97)
      .addCallback(toggleVisibility('delivery'), 6.1)

    // Delivery Text
    masterTimeline
      .addCallback(toggleVisibility('love'), 6.5)
      .addCallback(invert('love'), 6.95)
      .addCallback(invert('love'), 7.05)
      .addCallback(invert('love'), 7.65)
      .addCallback(invert('love'), 7.73)
      .addCallback(invert('love'), 7.86)
      .addCallback(toggleVisibility('love'), 8.5)

    // Finish
    masterTimeline
      .addCallback(toggleVisibility('creativity'), 10.55)

    this.masterTimeline = masterTimeline
  }
}
</script>



// WEBPACK FOOTER //
// src/components/HeroAnimation/HeroAnimation.vue